import React, { useEffect, useState } from "react";
import DynamicQuestionCell from "./DynamicQuestionCell";
import { useDashboardContext } from "../../context/customhook/dashboardHook";
import { appConstants } from "../../utils/constants";
import { getQuestionsMaster } from "../../services/api";
import categoryData from "../../utils/data/category.json";

export default function DynamicQuestions() {
  //The factual information will be stored within the variable named QuestionData.
  const [questionData, setQuestionData] = useState(null);

  //To display data after each selection, utilize the QuestionHistory feature.
  const [questionHistory, setQuestionHistory] = useState([]);

  //Once the service is loaded, the initial data for QuestionHistory is determined according to the update status.
  const [isUpdate, setIsUpdate] = useState(false);

  //We manage the display of the feedback option by controlling it through DashboardStates.
  const { dashboardStates, setDashboardStates } = useDashboardContext();

  //For both Arabic and English purposes.
  let currentLanguage = localStorage.getItem(appConstants.language);

  useEffect(() => {
    const findKeyByCategoryCode = (textValue) => {
      const item = categoryData.find(
        (obj) => obj.text.toLowerCase() === textValue.toLowerCase()
      );
      return item ? item.key : "Not Found";
    };

    const getStoreCode = () => {
      // Get store code from localStorage or dashboard states
      return (
        localStorage.getItem(appConstants.storeCode) ||
        dashboardStates.storeCode ||
        "default"
      );
    };

    //To retrieve questions from the service FetchQuestionData
    const fetchQuestionData = async (storeCode, categoryCode) => {
      try {
        await getQuestionsMaster(
          storeCode,
          localStorage.getItem(appConstants.conceptCode),
          categoryCode
        ).then((response) => {
          if (response?.status === 200 || response?.status === 201) {
            setQuestionData(response?.data);
          }
        });
        //Resetting the QuestionHistory on every rating change.
        setQuestionHistory([]);

        setIsUpdate(true);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (
      dashboardStates.rating !== undefined &&
      dashboardStates.rating !== null
    ) {
      setQuestionHistory([]);
      setQuestionData(null);

      const categoryCode = findKeyByCategoryCode(
        localStorage.getItem(appConstants.surveyType)
      );

      const storeCode = getStoreCode();

      if (dashboardStates.rating >= 0 && dashboardStates.rating <= 10) {
        const suffix =
          dashboardStates.rating <= 6 ? 1 : dashboardStates.rating <= 8 ? 2 : 3;
        fetchQuestionData(storeCode, categoryCode + "" + suffix);
      }
    }
  }, [dashboardStates.rating, dashboardStates.storeCode, setDashboardStates]);

  //The click event for each Question selection.
  const handleQuestionClick = (selectedValue) => {
    questionHistory.forEach((questions) => {
      questions.questions.forEach((questionInfo) => {
        if (
          questionInfo.level >= selectedValue.level &&
          questionInfo.selectedId !== -1
        ) {
          questionInfo.selectedId = -1;
        }
      });
    });

    //Setting the selected option
    selectedValue.selectedId = selectedValue.questionId;

    const hasQuestions =
      selectedValue.questions !== undefined &&
      selectedValue.questions.length > 0;

    if (hasQuestions) {
      const updatedHistory =
        selectedValue.level >= questionHistory.length
          ? [
              //Add questions if they are not already included.
              ...questionHistory,
              {
                questions: selectedValue.questions,
                headerTitles: selectedValue,
              },
            ]
          : [
              //Reset subsequent options upon changing the question.
              ...questionHistory.slice(0, selectedValue.level),
              {
                questions: selectedValue.questions,
                headerTitles: selectedValue,
              },
            ];

      setQuestionHistory(updatedHistory);
    } else {
      //To Remove unnecessary empty blocks.
      setQuestionHistory((prevQuestionHistory) => {
        return prevQuestionHistory.slice(0, selectedValue.level);
      });
    }

    //Display and conceal control for feedback based on the availability of sub-questions.
    setDashboardStates((previousState) => {
      return {
        ...previousState,
        showAdditionalFeedback: !hasQuestions,
      };
    });
  };

  useEffect(() => {
    //The initial data for QuestionHistory is established.
    if (isUpdate) {
      setIsUpdate(false);

      let questions = questionData?.data.questions.filter(
        (item) => item.optionEnglish !== null
      );

      let headerTitleInfo = questionData?.data.questions.find(
        (item) => item.optionEnglish === null
      ); // Filter items where optionEnglish is null

      setQuestionHistory((prevQuestionHistory) => [
        ...prevQuestionHistory,
        {
          //Here's a basic structure for QuestionHistory
          questions: questions,
          headerTitles: headerTitleInfo,
        },
      ]);
    }
  }, [isUpdate, questionData?.data, currentLanguage, dashboardStates.rating]);

  return (
    questionHistory.length > 0 &&
    questionHistory.map((item, index) => (
      //Passing data to the Question Cell as a param.
      <DynamicQuestionCell
        key={index}
        headerTitles={item.headerTitles}
        questions={item.questions}
        onQuestionClick={handleQuestionClick}
        language={currentLanguage}
      />
    ))
  );
}
